import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft, HardHat, Calendar, User, Hash, Boxes, AlertTriangle, CheckCircle2,
  ShieldCheck, Clock, X, Pencil,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, Spinner, Button, EmptyState, Field, Input, Select, Textarea } from "@/components/ui";
import { AssetPhotoGallery } from "@/features/assets/AssetPhotoGallery";

// =============================================================================
// RiggingDetailScreen — /assets/rigging/:riggingId
// Full identity + lifecycle + compliance + inspection history for a single
// rigging item. Read-only for everyone; edit happens via the inspection form
// (Commit 2) or the inline edit cells on /assets. T-047.
// =============================================================================

interface RiggingFull {
  rigging_id:          string;
  rigging_code:        string;
  display_name:        string;
  equipment_class:     string | null;
  equipment_type:      string | null;
  make:                string | null;
  model:               string | null;
  serial_number:       string | null;
  batch_lot_number:    string | null;
  asset_tag:           string | null;
  swl_kg:              number | null;
  rated_load_kn:       number | null;
  manufacture_date:    string | null;
  date_of_manufacture: string | null;
  purchase_date:       string | null;
  purchase_supplier:   string | null;
  in_service_date:     string | null;
  service_life_years:  number | null;
  destroy_by_date:     string | null;
  retirement_reason:   string | null;
  inspection_required: boolean | null;
  inspection_interval_months: number | null;
  last_inspection_date:  string | null;
  next_inspection_date:  string | null;
  last_result:           string | null;
  tag_colour:            string | null;
  tag_number:            string | null;
  kit_name:              string | null;
  status:                string;
  assigned_to_employee_id: string | null;
  assigned_from:         string | null;
  notes:                 string | null;
  photo_url:             string | null;
  created_at:            string;
  updated_at:            string;
  last_inspector?: { first_name: string | null; last_name: string | null } | null;
  assigned_to?:    { first_name: string | null; last_name: string | null } | null;
}

interface InspectionRow {
  inspection_id:     string;
  inspection_date:   string;
  inspection_type:   string | null;
  result:            string;
  tag_colour:        string | null;
  tag_number:        string | null;
  condition_notes:   string | null;
  findings:          string | null;
  defects_found:     boolean;
  defect_severity:   string | null;
  defect_summary:    string | null;
  action_required:   string | null;
  resolved:          boolean;
  resolved_date:     string | null;
  next_inspection_due: string | null;
  inspector_name:    string | null;
  inspector?:        { first_name: string | null; last_name: string | null } | null;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${dt.getDate().toString().padStart(2, "0")}/${(dt.getMonth() + 1).toString().padStart(2, "0")}/${dt.getFullYear()}`;
  } catch { return d; }
}

function daysBetween(d: string | null): number | null {
  if (!d) return null;
  const target = new Date(d).getTime();
  const now = new Date().setHours(0, 0, 0, 0);
  return Math.floor((target - now) / (1000 * 60 * 60 * 24));
}

function fullName(e: { first_name: string | null; last_name: string | null } | null | undefined): string | null {
  if (!e) return null;
  const n = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
  return n || null;
}

// -----------------------------------------------------------------------------

export default function RiggingDetailScreen() {
  const { riggingId } = useParams<{ riggingId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === "director" || user?.role === "manager";
  const [editOpen, setEditOpen] = useState(false);

  const { data: item, isLoading, error, refetch } = useQuery({
    queryKey: ["rigging_detail", riggingId],
    enabled: !!riggingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rigging_equipment")
        .select(`
          rigging_id, rigging_code, display_name, equipment_class, equipment_type,
          make, model, serial_number, batch_lot_number, asset_tag,
          swl_kg, rated_load_kn,
          manufacture_date, date_of_manufacture, purchase_date, purchase_supplier,
          in_service_date, service_life_years, destroy_by_date, retirement_reason,
          inspection_required, inspection_interval_months,
          last_inspection_date, next_inspection_date, last_result,
          tag_colour, tag_number, kit_name, status,
          assigned_to_employee_id, assigned_from,
          notes, photo_url, created_at, updated_at,
          last_inspector:employees!rigging_equipment_last_inspector_employee_id_fkey(first_name, last_name),
          assigned_to:employees!rigging_equipment_assigned_to_employee_id_fkey(first_name, last_name)
        `)
        .eq("rigging_id", riggingId!)
        .single();
      if (error) throw error;
      return data as unknown as RiggingFull;
    },
  });

  const { data: inspections = [], isLoading: inspLoading } = useQuery({
    queryKey: ["rigging_inspections_for", riggingId],
    enabled: !!riggingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rigging_inspections")
        .select(`
          inspection_id, inspection_date, inspection_type, result,
          tag_colour, tag_number, condition_notes, findings,
          defects_found, defect_severity, defect_summary,
          action_required, resolved, resolved_date, next_inspection_due,
          inspector_name,
          inspector:employees!rigging_inspections_inspector_employee_id_fkey(first_name, last_name)
        `)
        .eq("rigging_id", riggingId!)
        .order("inspection_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as InspectionRow[];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees").select("employee_id, first_name, last_name")
        .eq("is_active", true).order("first_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (error || !item) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-red-400">
            Rigging item not found{error ? `: ${error instanceof Error ? error.message : String(error)}` : "."}
          </div>
        </Card>
      </div>
    );
  }

  const destroyDays = daysBetween(item.destroy_by_date);
  const nextInspDays = daysBetween(item.next_inspection_date);
  const statusTone: "ok" | "warning" | "critical" | "default" =
    item.status === "in_service" ? "ok" :
    item.status === "quarantined" ? "warning" :
    item.status === "destroyed" || item.status === "retired" ? "critical" : "default";

  return (
    <div className="p-6 max-w-5xl mx-auto pb-12">
      <button onClick={() => navigate("/assets")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Back to assets
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-text-muted font-mono">{item.rigging_code}</div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mt-0.5">
            <HardHat size={20} className="text-accent" />
            {item.display_name}
          </h1>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <Pill
              label={item.status.replace(/_/g, " ")}
              tone={statusTone === "ok" ? "ok" : statusTone === "warning" ? "warning" : statusTone === "critical" ? "critical" : "default"}
              small
            />
            {item.tag_colour && <TagBadge value={item.tag_colour} />}
            {item.equipment_class && <Pill label={item.equipment_class} tone="notice" small />}
            {item.kit_name && (
              <span className="inline-flex items-center gap-1 text-[11px] text-text-muted font-mono">
                <Boxes size={12} /> {item.kit_name}
              </span>
            )}
          </div>
        </div>
        {canEdit && (
          <Button variant="secondary" size="sm" icon={Pencil} onClick={() => setEditOpen(true)}>
            Edit details
          </Button>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <MiniStat
          icon={ShieldCheck}
          label="Last inspected"
          value={fmtDate(item.last_inspection_date)}
          sub={fullName(item.last_inspector) ?? (item.last_result ?? undefined)}
        />
        <MiniStat
          icon={Clock}
          label="Next inspection"
          value={fmtDate(item.next_inspection_date)}
          sub={nextInspDays != null ? (nextInspDays < 0 ? `${Math.abs(nextInspDays)}d overdue` : `in ${nextInspDays}d`) : undefined}
          tone={nextInspDays != null && nextInspDays < 0 ? "danger" : nextInspDays != null && nextInspDays < 15 ? "warn" : undefined}
        />
        <MiniStat
          icon={User}
          label="Assigned to"
          value={fullName(item.assigned_to) ?? "Pool"}
          sub={item.assigned_from ? `from ${fmtDate(item.assigned_from)}` : undefined}
        />
        <MiniStat
          icon={Calendar}
          label="Destroy by"
          value={fmtDate(item.destroy_by_date)}
          sub={destroyDays != null ? (destroyDays < 0 ? "expired" : `${destroyDays}d remaining`) : item.retirement_reason ?? undefined}
          tone={destroyDays != null && destroyDays < 0 ? "danger" : destroyDays != null && destroyDays < 90 ? "warn" : undefined}
        />
      </div>

      {/* Identity + Lifecycle + Compliance */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <Card>
          <div className="p-5">
            <SectionLabel icon={Hash}>Identity</SectionLabel>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <RO label="Class" value={item.equipment_class} />
              <RO label="Type" value={item.equipment_type} />
              <RO label="Make" value={item.make} />
              <RO label="Model" value={item.model} />
              <RO label="Serial number" value={item.serial_number} mono />
              <RO label="Batch / lot" value={item.batch_lot_number} mono />
              <RO label="Asset tag" value={item.asset_tag} mono />
              <RO label="Tag number" value={item.tag_number} mono />
            </dl>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <SectionLabel icon={Calendar}>Lifecycle</SectionLabel>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <RO label="Purchase date" value={fmtDate(item.purchase_date)} />
              <RO label="Supplier" value={item.purchase_supplier} />
              <RO label="Manufactured" value={fmtDate(item.date_of_manufacture ?? item.manufacture_date)} />
              <RO label="In service" value={fmtDate(item.in_service_date)} />
              <RO label="Service life" value={item.service_life_years ? `${item.service_life_years} yrs` : null} />
              <RO label="Destroy by" value={fmtDate(item.destroy_by_date)} />
              <RO label="SWL" value={item.swl_kg ? `${item.swl_kg} kg` : null} />
              <RO label="Rated load" value={item.rated_load_kn ? `${item.rated_load_kn} kN` : null} />
            </dl>
          </div>
        </Card>
      </div>

      <Card>
        <div className="p-5">
          <SectionLabel icon={ShieldCheck}>Compliance</SectionLabel>
          <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[13px]">
            <RO label="Inspection required" value={item.inspection_required ? "Yes" : "No"} />
            <RO label="Interval" value={item.inspection_interval_months ? `${item.inspection_interval_months} months` : null} />
            <RO label="Last result" value={item.last_result} />
            <RO label="Current tag" value={item.tag_colour} />
          </dl>
        </div>
      </Card>

      {item.notes && (
        <Card>
          <div className="p-5">
            <SectionLabel>Notes</SectionLabel>
            <div className="mt-2 text-[13px] text-text whitespace-pre-wrap">{item.notes}</div>
          </div>
        </Card>
      )}

      {/* Photos */}
      <div className="mt-6">
        <AssetPhotoGallery assetKind="rigging" assetId={item.rigging_id} canEdit={canEdit} />
      </div>

      {/* Inspection history */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[13px] font-medium text-text flex items-center gap-2">
            <ShieldCheck size={15} className="text-accent" />
            Inspection history
            <span className="text-[11px] text-text-muted font-normal">({inspections.length})</span>
          </div>
          {/* T-255 — point at the unified NewInspectionScreen so HR>Assets uses
              the same form as Portal>Inspect (class-aware checklist with
              pass/fail/na toggles), not the old single-result dropdown. The
              old route + RiggingInspectionScreen file are now orphan dead code
              — safe to delete in a follow-up commit after smoke. */}
          <Link to={`/inspections/new?item=${item.rigging_id}&kind=rigging`}>
            <Button variant="secondary" size="sm" icon={CheckCircle2}>New inspection</Button>
          </Link>
        </div>

        <Card>
          {inspLoading ? (
            <div className="p-6 flex justify-center"><Spinner /></div>
          ) : inspections.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No inspections recorded"
              description="Use the field app to log the first inspection. Coming in T-047 Commit 2."
            />
          ) : (
            <div className="divide-y divide-line">
              {inspections.map(i => <InspectionRow key={i.inspection_id} i={i} />)}
            </div>
          )}
        </Card>
      </div>

      {editOpen && (
        <RiggingDetailEditor
          item={item}
          employees={employees}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); refetch(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function SectionLabel({ icon: Icon, children }: { icon?: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
      {Icon && <Icon size={12} />}
      {children}
    </div>
  );
}

function RO({ label, value, mono = false }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className={`text-text ${mono ? "font-mono text-[12px]" : ""}`}>
        {value === null || value === undefined || value === "" ? <span className="text-text-dim">—</span> : value}
      </dd>
    </>
  );
}

function MiniStat({
  icon: Icon, label, value, sub, tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  tone?: "warn" | "danger";
}) {
  const valueColour = tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : "text-text";
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
          <Icon size={12} />
          {label}
        </div>
        <div className={`mt-1 text-[15px] ${valueColour}`}>{value}</div>
        {sub && <div className="text-[11px] text-text-muted mt-0.5">{sub}</div>}
      </div>
    </Card>
  );
}

function TagBadge({ value }: { value: string }) {
  const key = value.toLowerCase();
  const style =
    key === "red"    ? "bg-danger/15 text-danger border-danger/40" :
    key === "green"  ? "bg-ok/15 text-ok border-ok/40" :
    key === "blue"   ? "bg-info/15 text-info border-info/40" :
    key === "yellow" ? "bg-warn/15 text-warn border-warn/40" :
                       "bg-text/8 text-text-muted border-line";
  const dot =
    key === "red"    ? "bg-danger" :
    key === "green"  ? "bg-ok" :
    key === "blue"   ? "bg-info" :
    key === "yellow" ? "bg-warn" :
                       "bg-text-muted";
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider2 ${style}`}>
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
      {value} tag
    </span>
  );
}

function InspectionRow({ i }: { i: InspectionRow }) {
  const resultLower = (i.result ?? "").toLowerCase();
  const resultTone =
    resultLower === "pass" ? "ok" :
    resultLower === "restricted use" ? "warning" :
    resultLower === "fail" || resultLower === "quarantine" || resultLower === "destroy" ? "critical" :
    "default";
  const inspectorName = fullName(i.inspector) ?? i.inspector_name ?? "Unknown";

  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-mono text-text">{fmtDate(i.inspection_date)}</span>
          <Pill label={i.result} tone={resultTone} small />
          {i.tag_colour && <TagBadge value={i.tag_colour} />}
          {i.inspection_type && i.inspection_type !== "Detailed" && (
            <Pill label={i.inspection_type} tone="default" small />
          )}
          {i.defects_found && (
            <span className="inline-flex items-center gap-1 text-[11px] text-warn">
              <AlertTriangle size={11} /> defect
            </span>
          )}
        </div>
        <span className="text-[11px] text-text-muted">by {inspectorName}</span>
      </div>
      {(i.condition_notes || i.findings) && (
        <div className="mt-1.5 text-[12px] text-text-muted">
          {i.condition_notes || i.findings}
        </div>
      )}
      {i.defects_found && (i.defect_summary || i.action_required) && (
        <div className="mt-1.5 p-2 rounded bg-warn/10 border border-warn/30 text-[12px]">
          {i.defect_summary && <div className="text-text">{i.defect_summary}</div>}
          {i.action_required && <div className="text-text-muted mt-0.5">Action: {i.action_required}</div>}
          {i.resolved && i.resolved_date && (
            <div className="text-ok mt-0.5">Resolved {fmtDate(i.resolved_date)}</div>
          )}
        </div>
      )}
      {i.next_inspection_due && (
        <div className="mt-1 text-[11px] text-text-muted">
          Next due {fmtDate(i.next_inspection_due)}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Edit-details modal — manager+ can change allocation, assignee, status, notes
// after creation. Mirrors the QuoteHeaderEditor pattern.
// -----------------------------------------------------------------------------

const EDIT_STATUSES = ["in_service", "quarantined", "destroyed", "retired"] as const;

function RiggingDetailEditor({ item, employees, onClose, onSaved }: any) {
  const [kit_name, setKitName] = useState<string>(item.kit_name ?? "");
  const [assigned_to_employee_id, setAssignedTo] = useState<string>(item.assigned_to_employee_id ?? "");
  const [status, setStatus] = useState<string>(item.status ?? "in_service");
  const [notes, setNotes] = useState<string>(item.notes ?? "");
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("rigging_equipment")
        .update({
          kit_name: kit_name.trim() || null,
          assigned_to_employee_id: assigned_to_employee_id || null,
          status,
          notes: notes.trim() || null,
        })
        .eq("rigging_id", item.rigging_id);
      if (error) throw error;
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => {
      const msg = e?.message ?? e?.error?.message ?? e?.details ?? JSON.stringify(e);
      setErrMsg(`Save failed — ${msg}`);
    },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-white w-full md:max-w-md md:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">Edit rigging details</h3>
          <button onClick={onClose} type="button"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {errMsg && (
            <div className="px-3 py-2 rounded bg-red-50 border border-red-200 text-red-700 text-[12px]">{errMsg}</div>
          )}
          <Field label="Kit / allocation">
            <Input value={kit_name} onChange={e => setKitName(e.target.value)} placeholder="e.g. Rigging Kit 4" />
          </Field>
          <Field label="Assigned to">
            <Select value={assigned_to_employee_id} onChange={e => setAssignedTo(e.target.value)}>
              <option value="">— Pool —</option>
              {employees.map((e: any) => {
                const name = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
                return <option key={e.employee_id} value={e.employee_id}>{name}</option>;
              })}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={status} onChange={e => setStatus(e.target.value)}>
              {EDIT_STATUSES.map(s => <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>)}
            </Select>
          </Field>
          <Field label="Notes">
            <Textarea rows={3} value={notes} onChange={(e: any) => setNotes(e.target.value)} />
          </Field>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2 sticky bottom-0">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button icon={CheckCircle2} onClick={() => saveMut.mutate()} disabled={saveMut.isPending} type="button">
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
